import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { DriverLocation } from '../lib/types';

interface UseDriverLocationsResult {
  locations: DriverLocation[];
  loading: boolean;
  error: string | null;
}

export function useDriverLocations(companyId?: string | null): UseDriverLocationsResult {
  const [locations, setLocations] = useState<DriverLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchLocations() {
      let query = supabase
        .from('driver_locations')
        .select('*, driver:drivers!inner(id, full_name, status, vehicle_id, company_id)');

      if (companyId) {
        query = query.eq('driver.company_id', companyId);
      }

      const { data, error: err } = await query;

      if (err) {
        setError(err.message);
      } else {
        setLocations((data as DriverLocation[]) ?? []);
      }
      setLoading(false);
    }

    fetchLocations();

    const channel = supabase
      .channel(`driver-locations-realtime-${companyId ?? 'all'}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'driver_locations' },
        (payload) => {
          setLocations((prev) => {
            const updated = payload.new as DriverLocation & {
              driver?: { company_id?: string | null };
            };
            if (companyId && updated.driver?.company_id !== companyId) {
              return prev;
            }
            const idx = prev.findIndex((l) => l.driver_id === updated.driver_id);
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = { ...next[idx], ...updated };
              return next;
            }
            if (!companyId || updated.driver?.company_id === companyId) {
              return [...prev, updated];
            }
            return prev;
          });
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [companyId]);

  return { locations, loading, error };
}
